import React, { useState } from 'react';
import { Box, Container, Breadcrumb } from '@legion-ui/core';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  usePublisherJourneyById,
  useUpdatePublisherJourneyStatus,
} from '@/hooks/usePublisherJourneyQuery';
import { useUserByUsername } from '@/hooks/useUserByUsername';
import { userStore } from '@/store/users';
import Indicator from '@/components/Indicator';
import Button from '@/components/Button';
import UserInfo from '@/components/UserInfo';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import RejectReasonDialog from '@/components/RejectReasonDialog';
import LoadingOverlay from '@/components/LoadingOverlay';
import useLoadingStore from '@/store/loading';
import { notify } from '@/services/notify';
import { ToastContainer } from 'react-toastify';
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaUser,
  FaCalendar,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PublisherJourneyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showMasked, setShowMasked] = useState(true);

  const {
    data: request,
    isLoading,
    isError,
    error,
  } = usePublisherJourneyById(id!);

  // Fetch user data based on username from request
  const {
    user: userData,
    isLoading: userLoading,
    isError: userError,
  } = useUserByUsername(request?.username || '', undefined);

  // Dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // Loading overlay store
  const { setLoading } = useLoadingStore();

  // Get current logged-in user
  const [currentUser] = userStore((state) => [state.userLogedIn]);

  // Mutation hook for updating status
  const updateStatusMutation = useUpdatePublisherJourneyStatus();

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate2 = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
  };

  const maskText = (text: string, visibleChars: number = 4) => {
    if (!text || !showMasked) return text;

    // Untuk text yang sangat pendek, tetap mask minimal 2 karakter pertama
    if (text.length <= 3) {
      return '*'.repeat(text.length - 1) + text.slice(-1);
    }

    // Untuk text pendek (4-6 karakter), tampilkan maksimal 50% karakter terakhir
    if (text.length <= visibleChars + 2) {
      const charsToShow = Math.max(1, Math.floor(text.length / 2));
      const maskedLength = text.length - charsToShow;
      const masked = '*'.repeat(maskedLength);
      return masked + text.slice(-charsToShow);
    }

    // Untuk text panjang, gunakan visibleChars
    const maskedLength = text.length - visibleChars;
    const masked = '*'.repeat(Math.min(maskedLength, 10)); // Maximum 10 asterisks
    return masked + text.slice(-visibleChars);
  };

  const toggleMasking = () => {
    setShowMasked(!showMasked);
  };

  // Handler for approve action
  const handleApprove = () => {
    setLoading(true); // Show loading overlay
    const user = userData?.[0]; // Get first user from userData
    updateStatusMutation.mutate(
      {
        id: id!,
        data: {
          status: 'APPROVED',
          executedBy: currentUser?.username || currentUser?.email || 'system',
          userEmail: user?.email,
          fullname: user?.info?.fullname,
          username: user?.username,
          executionReason: 'Approved after verification',
        },
      },
      {
        onSuccess: () => {
          setShowApproveDialog(false);
          notify('Request approved successfully!', { type: 'success' });

          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            setLoading(false); // Hide loading overlay
            navigate('/requests', { replace: true }); // Navigate back to requests list with replace
          }, 2000);
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to approve request:', error);
          notify('Failed to approve request. Please try again.', {
            type: 'error',
          });
        },
      },
    );
  }; // Handler for reject action
  const handleReject = (reason: string) => {
    setLoading(true); // Show loading overlay
    const user = userData?.[0]; // Get first user from userData
    updateStatusMutation.mutate(
      {
        id: id!,
        data: {
          status: 'REJECTED',
          executedBy: currentUser?.username || currentUser?.email || 'system',
          userEmail: user?.email,
          fullname: user?.info?.fullname,
          username: user?.username,
          executionReason: reason,
        },
      },
      {
        onSuccess: () => {
          setShowRejectDialog(false);
          notify('Request rejected successfully!', { type: 'success' });

          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            setLoading(false); // Hide loading overlay
            navigate('/requests', { replace: true }); // Navigate back to requests list with replace
          }, 2000);
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to reject request:', error);
          notify('Failed to reject request. Please try again.', {
            type: 'error',
          });
        },
      },
    );
  };

  if (isError) {
    return (
      <article className="min-h-screen py-8">
        <Container>
          <div className="text-center py-16">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Request Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error instanceof Error
                ? error.message
                : 'The requested publisher journey could not be found.'}
            </p>
            <Button
              onClick={() => navigate('/requests')}
              iconLeft={<FaArrowLeft />}
            >
              Back to Requests
            </Button>
          </div>
        </Container>
      </article>
    );
  }

  return (
    <article className="min-h-screen py-8">
      <Container>
        {/* Header & Breadcrumb */}
        <Box as="header" className="mb-6">
          <Breadcrumb
            className="mb-4"
            items={[
              {
                label: (
                  <Link
                    to="/requests"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Requests List
                  </Link>
                ),
              },
              {
                label: isLoading ? 'Loading...' : `Request As Publisher`,
              },
            ]}
          />

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isLoading ? <Skeleton width={300} /> : `Request As Publisher`}
              </h1>
              <p className="text-gray-600 mt-1">
                {isLoading ? (
                  <Skeleton width={200} />
                ) : (
                  'View and manage publisher request details'
                )}
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() => navigate('/requests')}
              iconLeft={<FaArrowLeft />}
            >
              Back to List
            </Button>
          </div>
        </Box>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Request Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Information */}
            <div className="bg-white rounded-lg shadow border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <FaUser className="mr-2 text-blue-500" />
                  User Information
                </h2>
                <button
                  onClick={toggleMasking}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {showMasked ? <FaEye /> : <FaEyeSlash />}
                  {showMasked ? 'Show Data' : 'Hide Data'}
                </button>
              </div>

              {userLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="flex gap-2">
                      <Skeleton height={20} width={100} />
                      <Skeleton height={20} width={200} />
                    </div>
                  ))}
                </div>
              ) : userError ? (
                <div className="text-red-600 p-4 bg-red-50 rounded-lg">
                  Error loading user information
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 flex justify-center mb-4">
                    <div className="w-24 h-24">
                      <img
                        src={
                          userData?.[0]?.info?.avatar || '/assets/avatar.png'
                        }
                        alt="user-avatar"
                        crossOrigin="anonymous"
                        className="object-cover w-full h-full rounded-full"
                      />
                    </div>
                  </div>

                  <UserInfo
                    label="Fullname:"
                    value={maskText(userData?.[0]?.info?.fullname || '-', 4)}
                  />
                  <UserInfo
                    label="Username:"
                    value={maskText(userData?.[0]?.username || '-', 4)}
                  />
                  <UserInfo
                    label="Email:"
                    value={maskText(userData?.[0]?.email || '-', 6)}
                  />
                  <UserInfo
                    label="No Handphone:"
                    value={maskText(
                      userData?.[0]?.info?.phone_number || '-',
                      4,
                    )}
                  />
                  <UserInfo
                    label="Gender:"
                    value={userData?.[0]?.info?.gender || '-'}
                  />
                  <UserInfo
                    label="Country:"
                    value={userData?.[0]?.info?.country || '-'}
                  />
                  <UserInfo
                    label="Street Address:"
                    value={maskText(userData?.[0]?.info?.address || '-', 8)}
                  />
                </div>
              )}
            </div>

            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FaUser className="mr-2 text-blue-500" />
                Request Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <div className="text-lg font-mono bg-gray-50 p-2 rounded">
                    {isLoading ? <Skeleton /> : request?.username}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div>
                    {isLoading ? (
                      <Skeleton height={32} width={100} />
                    ) : (
                      <Indicator status={request?.status as any} />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How Found Us
                  </label>
                  <div className="text-gray-900">
                    {isLoading ? <Skeleton /> : request?.foundUsBy || '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request Reason
                  </label>
                  <div className="bg-gray-50 p-2 rounded max-h-24 overflow-y-auto">
                    {isLoading ? (
                      <div>
                        <Skeleton count={2} />
                      </div>
                    ) : (
                      <p className="text-gray-900 text-sm whitespace-pre-wrap">
                        {request?.reason || 'No reason provided'}
                      </p>
                    )}
                  </div>
                </div>

                {request?.status === 'REJECTED' && request?.executionReason && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason
                    </label>
                    <div className="bg-red-50 p-3 rounded border border-red-200">
                      {isLoading ? (
                        <div>
                          <Skeleton count={2} />
                        </div>
                      ) : (
                        <p className="text-red-900 text-sm whitespace-pre-wrap">
                          {request?.executionReason}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Remove the separate Request Reason section */}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FaCalendar className="mr-2 text-blue-500" />
                Timeline
              </h2>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Request Created
                    </p>
                    <p className="text-sm text-gray-500">
                      {isLoading ? (
                        <Skeleton width={150} />
                      ) : (
                        formatDate2(request?.createdAt)
                      )}
                    </p>
                  </div>
                </div>

                {request?.executedBy && (
                  <div className="flex items-start space-x-3">
                    <div
                      className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                        request?.status === 'APPROVED'
                          ? 'bg-green-500'
                          : request?.status === 'REJECTED'
                            ? 'bg-red-500'
                            : 'bg-gray-500'
                      }`}
                    ></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {request?.status === 'APPROVED'
                          ? 'Request Approved'
                          : request?.status === 'REJECTED'
                            ? 'Request Rejected'
                            : 'Request Processed'}
                      </p>
                      <p className="text-sm text-gray-500">
                        By {request.executedBy}
                      </p>
                      <p className="text-sm text-gray-500">
                        At {formatDate(request.executedAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {request?.status === 'PENDING' && (
              <div className="bg-white rounded-lg shadow border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Actions
                </h2>

                <div className="space-y-3">
                  <Button
                    className="w-full justify-center"
                    iconLeft={<FaCheck />}
                    onClick={() => setShowApproveDialog(true)}
                    disabled={updateStatusMutation.isLoading}
                    loading={updateStatusMutation.isLoading}
                  >
                    {updateStatusMutation.isLoading
                      ? 'Approving...'
                      : 'Approve Request'}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-center text-red-600 border-red-300 hover:bg-red-50"
                    iconLeft={<FaTimes />}
                    onClick={() => setShowRejectDialog(true)}
                    disabled={updateStatusMutation.isLoading}
                    loading={updateStatusMutation.isLoading}
                  >
                    {updateStatusMutation.isLoading
                      ? 'Rejecting...'
                      : 'Reject Request'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* Confirmation Dialog for Approve */}
      <ConfirmationDialog
        isOpen={showApproveDialog}
        onClose={() => setShowApproveDialog(false)}
        onConfirm={handleApprove}
        title="Approve Request"
        message={`Are you sure you want to approve this publisher request for ${request?.username}?`}
        confirmText="Approve"
        confirmColor="success"
        isLoading={updateStatusMutation.isLoading}
      />

      {/* Reject Reason Dialog */}
      <RejectReasonDialog
        isOpen={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onConfirm={handleReject}
        isLoading={updateStatusMutation.isLoading}
      />

      {/* Loading Overlay */}
      <LoadingOverlay />

      {/* Toast Container */}
      <ToastContainer />
    </article>
  );
};

export default PublisherJourneyDetail;
